import {Chip, Modal, ModalBody, ModalContent, ModalHeader} from "@heroui/react";
import {Icon} from "@iconify/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import React, {useState} from "react";
import {apiActionImproved, apiRetrieveImproved} from "../../../api";
import {config} from "../../../config/config.ts";
import {LeagueStatusText, SportTypes} from "../../../constants.ts";
import PInput from "../../../components/PInput.tsx";
import Header from "../../../Header.tsx";

export const Route = createFileRoute('/_app/master/events')({
    component: RouteComponent,
})

// Mirrors MasterController::events — every tournament/league platform-wide, newest first.
type EventRow = {
    kind: 'tournament' | 'league';
    uid: string;
    slug: string | null;
    title: string | null;
    type_label: string;
    sport_type: string | null;
    status: number | null;
    owner_uid: string | null;
    owner_name: string | null;
    created_at: string;
    entrants: number;
    /** Finished games only. For a season this includes every division's games. */
    games: number;
    /** Where those games were played, biggest first. */
    venues: {name: string; games: number}[];
    /** A master's veto: kept off the homepage's "Played on ScorePal" carousel. */
    hidden_from_showcase?: boolean;
};

const TYPE_FILTERS: {value: string; label: string}[] = [
    {value: '',          label: 'All'},
    {value: 'tournament', label: 'Tournaments'},
    {value: 'league',     label: 'Leagues'},
    {value: 'season',     label: 'Seasons'},
    {value: 'ladder',     label: 'Ladders'},
    {value: 'americano',  label: 'Americanos'},
    {value: 'mexicano',   label: 'Mexicanos'},
    {value: 'kotc',       label: 'KOTC'},
];

// Full venue split for one event — opened from the Venues column when an event spans more than
// one location, where a single cell can't show the whole picture.
function VenueBreakdownModal({row, onClose}: {row: EventRow | null; onClose: () => void}) {
    if (!row) return null;
    const total = row.venues.reduce((sum, v) => sum + v.games, 0);

    return (
        <Modal isOpen onClose={onClose} size="md">
            <ModalContent>
                <ModalHeader className="flex-col items-start gap-0.5">
                    <span>{row.title || row.uid}</span>
                    <span className="text-xs font-normal text-default-500">
                        {total} finished {total === 1 ? 'game' : 'games'} across {row.venues.length} venues
                        {row.type_label === 'Season' && ' (all divisions)'}
                    </span>
                </ModalHeader>
                <ModalBody className="pb-6">
                    <div className="flex flex-col gap-3">
                        {row.venues.map((v) => {
                            const share = total > 0 ? Math.round((v.games / total) * 100) : 0;
                            return (
                                <div key={v.name} className="flex flex-col gap-1">
                                    <div className="flex items-baseline justify-between gap-3 text-sm">
                                        <span className="font-medium">{v.name}</span>
                                        <span className="shrink-0 tabular-nums text-default-500">
                                            {v.games} <span className="text-default-400">({share}%)</span>
                                        </span>
                                    </div>
                                    <div className="h-1.5 overflow-hidden rounded-full bg-default-100">
                                        <div className="h-full rounded-full bg-primary" style={{width: `${share}%`}} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

/** One card on the homepage's "Played on ScorePal" carousel, as its public feed sends it. */
type ShowcaseCard = {type: 'tournament' | 'league'; uid: string; title: string; entrants: number; entrant_kind: 'players' | 'teams'; games_played?: number; club_name: string | null; organizer: string | null; status?: string};
type HiddenEvent = {kind: 'tournament' | 'league'; uid: string; slug: string | null; title: string | null; sport_type: string | null; owner_name: string | null};

/**
 * The homepage carousel, from the master's side: exactly what is on it right now, each with a
 * Hide button, and everything that has been hidden, each with a way back.
 *
 * It exists because the carousel leads on the biggest fields, and the biggest fields include
 * things that are not competitions — a 1024-team bracket built to see how far it goes, a "FIFA
 * World Cup 2100". They pass every automatic check, so the filter is a person. Reading the same
 * public feed the homepage reads means there is no second opinion about what is showing.
 */
function HomepageShowcase({onChanged}: {onChanged: (uid: string, hidden: boolean) => void}) {
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState<string | null>(null);
    const [open, setOpen] = useState(true);

    const showing = useQuery<{events: ShowcaseCard[]}>({
        queryKey: ['master-showcase-showing'],
        queryFn:  () => fetch(config.apiUrl + '/public-live-events', {headers: {Accept: 'application/json'}}).then((r) => r.json()),
    });
    const hidden = useQuery<{hidden: HiddenEvent[]}>({
        queryKey: ['master-showcase-hidden'],
        queryFn:  () => apiRetrieveImproved('master/events/showcase' as any, {}),
    });

    const setHidden = async (kind: 'tournament' | 'league', uid: string, title: string | null, hide: boolean) => {
        setBusy(uid);
        try {
            const res: any = await apiActionImproved('master/events/showcase' as any, {kind, uid, hidden: hide});
            queryClient.setQueryData(['master-showcase-hidden'], {hidden: res?.hidden ?? []});
            // The server dropped its cached rail, so this refetch is the rebuilt homepage.
            await queryClient.invalidateQueries({queryKey: ['master-showcase-showing']});
            onChanged(uid, hide); // keep the table below in step
            addToast({title: hide ? `"${title || uid}" is off the homepage` : `"${title || uid}" can show on the homepage again`, color: 'success'});
        } catch (e: any) {
            addToast({title: e?.message || 'Could not save that', color: 'danger'});
        } finally {
            setBusy(null);
        }
    };

    const cards = showing.data?.events ?? [];
    const hiddenRows = hidden.data?.hidden ?? [];

    return (
        <div className="mb-5 rounded-xl border border-default-200 bg-content1 p-4" data-homepage-showcase>
            <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 text-left">
                <span>
                    <span className="text-sm font-bold uppercase tracking-wide">On the homepage carousel</span>
                    <span className="ml-2 text-xs text-default-500">
                        {showing.isLoading ? 'loading…' : `${cards.length} showing`}{hiddenRows.length > 0 ? ` · ${hiddenRows.length} hidden` : ''}
                    </span>
                </span>
                <span className="text-xs text-primary">{open ? 'Collapse' : 'Expand'}</span>
            </button>

            {open && (
                <>
                    <p className="mb-3 mt-1 text-xs text-default-500">
                        "Played on ScorePal", in the order visitors see it: biggest fields first, a few smaller ones mixed in.
                        Hide anything that is not a real competition. It only leaves the homepage — the event, its public page
                        and its organizer are untouched — and the homepage updates straight away.
                    </p>

                    {showing.isLoading
                        ? <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                        : cards.length === 0
                            ? <div className="py-2 text-sm text-default-500">Nothing is on the carousel right now.</div>
                            : (
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                    {cards.map((c, i) => (
                                        <div key={`${c.type}-${c.uid}`} className="flex items-center gap-3 rounded-lg border border-default-200 px-3 py-2">
                                            <span className="w-5 shrink-0 text-right text-xs tabular-nums text-default-400">{i + 1}</span>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium" title={c.title}>{c.title}</div>
                                                <div className="truncate text-xs text-default-500">
                                                    {c.entrants} {c.entrant_kind} · {c.games_played ?? 0} games · {c.club_name || c.organizer || '—'}
                                                </div>
                                            </div>
                                            <button type="button" disabled={busy === c.uid} onClick={() => setHidden(c.type, c.uid, c.title, true)}
                                                    className="shrink-0 rounded-full bg-default-100 px-3 py-1 text-xs font-semibold text-default-700 hover:bg-danger-100 hover:text-danger-700 disabled:opacity-50">
                                                {busy === c.uid ? '…' : 'Hide'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                    {hiddenRows.length > 0 && (
                        <div className="mt-4">
                            <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-default-500">Hidden from the homepage</div>
                            <div className="flex flex-wrap gap-2">
                                {hiddenRows.map((h) => (
                                    <span key={`${h.kind}-${h.uid}`} className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-50 py-1 pl-3 pr-1 text-xs">
                                        <span className="max-w-[240px] truncate" title={`${h.title || h.uid} — ${h.owner_name || ''}`}>{h.title || h.uid}</span>
                                        <button type="button" disabled={busy === h.uid} onClick={() => setHidden(h.kind, h.uid, h.title, false)}
                                                className="rounded-full bg-white px-2 py-0.5 font-semibold text-primary hover:bg-primary hover:text-white disabled:opacity-50 dark:bg-default-100">
                                            {busy === h.uid ? '…' : 'Show again'}
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function RouteComponent() {
    const queryClient = useQueryClient();
    const [type, setType] = useState('');
    const [search, setSearch] = useState('');
    const [venueRow, setVenueRow] = useState<EventRow | null>(null);
    // Rows flipped from the table since the list loaded — the list is paged and accumulated, so the
    // honest way to show a flip is to remember it rather than refetch and lose the reader's place.
    const [hiddenOverride, setHiddenOverride] = useState<Record<string, boolean>>({});
    const [flipping, setFlipping] = useState<string | null>(null);

    const flipRow = async (r: EventRow, hide: boolean) => {
        setFlipping(r.uid);
        try {
            await apiActionImproved('master/events/showcase' as any, {kind: r.kind, uid: r.uid, hidden: hide});
            setHiddenOverride((m) => ({...m, [r.uid]: hide}));
            queryClient.invalidateQueries({queryKey: ['master-showcase-showing']});
            queryClient.invalidateQueries({queryKey: ['master-showcase-hidden']});
            addToast({title: hide ? `"${r.title || r.uid}" is off the homepage` : `"${r.title || r.uid}" can show on the homepage again`, color: 'success'});
        } catch (e: any) {
            addToast({title: e?.message || 'Could not save that', color: 'danger'});
        } finally {
            setFlipping(null);
        }
    };
    // Only hit the API once typing pauses — the list is capped at 500 rows server-side.
    const [committedSearch, setCommittedSearch] = useState('');
    React.useEffect(() => {
        const t = setTimeout(() => setCommittedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    // Paged 15 at a time — the first screen paints fast and "Show more" appends the next slice.
    const [offset, setOffset] = useState(0);
    const [accumulated, setAccumulated] = useState<EventRow[]>([]);
    React.useEffect(() => { setOffset(0); setAccumulated([]); }, [type, committedSearch]);

    const query = useQuery<{rows: EventRow[]; has_more?: boolean}>({
        queryKey: ['master-events', type, committedSearch, offset],
        queryFn:  () => apiRetrieveImproved('master/events' as any,
            {type: type || undefined, search: committedSearch || undefined, offset, limit: 15}),
    });

    React.useEffect(() => {
        if (!query.data) return;
        // Dedup by uid so a StrictMode re-run or refetch can never double-append a page.
        setAccumulated((prev) => {
            if (offset === 0) return query.data!.rows;
            const seen = new Set(prev.map((r) => r.uid));
            return [...prev, ...query.data!.rows.filter((r) => !seen.has(r.uid))];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data]);

    const rows = accumulated.length || offset > 0 ? accumulated : (query.data?.rows || []);
    const hasMore = query.data?.has_more === true;

    return (
        <>
            <Header />
            <div className="mx-auto w-11/12 max-w-6xl py-4">
                <h1 className="mb-1 text-2xl font-bold">Events</h1>
                <p className="mb-3 text-sm text-default-500">
                    Every tournament and league on the platform (americanos, mexicanos and KOTC are leagues
                    under the hood). Season divisions are folded into their parent. Newest first, 15 at a time.
                </p>

                <HomepageShowcase onChanged={(uid, hidden) => setHiddenOverride((m) => ({...m, [uid]: hidden}))} />

                {/* Search sits with the filters it combines with: "Tournaments" + "sam" finds Sam's
                    tournaments. It reaches the whole platform, not just the rows loaded so far. */}
                <div className="mb-2">
                    <PInput aria-label="Search tournaments and leagues" size="md" isClearable
                            placeholder="Search tournaments and leagues — title, organizer name or email, club, sport or ID"
                            startContent={<Icon icon="material-symbols-light:search" className="text-xl text-default-400" />}
                            value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} />
                </div>
                <div className="mb-4 flex flex-wrap gap-1.5">
                    {TYPE_FILTERS.map((f) => (
                        <button key={f.value} type="button" onClick={() => setType(f.value)}
                                className={'rounded-full px-3 py-1 text-xs font-semibold transition ' +
                                    (f.value === type ? 'bg-primary text-white' : 'bg-default-100 text-default-600 hover:bg-default-200')}>
                            {f.label}
                        </button>
                    ))}
                </div>

                {query.isLoading && offset === 0 ? (
                    <div className="flex justify-center py-12"><Spinner size="lg" /></div>
                ) : rows.length === 0 ? (
                    <div className="py-12 text-center text-default-500">No events match.</div>
                ) : (
                    <div className="overflow-x-auto rounded-xl border border-default-200">
                        <table className="w-full min-w-[1340px] text-sm">
                            <thead>
                                <tr className="border-b border-default-200 bg-default-50 text-left text-xs uppercase tracking-wider text-default-500">
                                    <th className="px-3 py-2">Created</th>
                                    <th className="px-3 py-2">Title</th>
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2">Sport</th>
                                    <th className="px-3 py-2">Owner</th>
                                    <th className="px-3 py-2">Entrants</th>
                                    <th className="px-3 py-2">Games</th>
                                    <th className="px-3 py-2">Venues</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2" title="Whether it may appear on the homepage's Played on ScorePal carousel">Homepage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={`${r.kind}-${r.uid}`} className="border-b border-default-100 last:border-0">
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">
                                            {new Date(r.created_at).toLocaleDateString(undefined, {day: '2-digit', month: 'short', year: '2-digit'})}
                                        </td>
                                        <td className="max-w-[260px] truncate px-3 py-2 font-medium">
                                            <a href={`${config.publicUrl}/e/${r.slug || r.uid}`} target="_blank" rel="noreferrer"
                                               className="text-primary hover:underline" title={r.title || r.uid}>
                                                {r.title || <span className="text-default-400">(untitled)</span>}
                                            </a>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <Chip size="sm" variant="flat"
                                                  color={r.kind === 'tournament' ? 'secondary'
                                                      : ['Americano', 'Mexicano', 'King of the Court'].includes(r.type_label) ? 'warning' : 'primary'}>
                                                {r.type_label}
                                            </Chip>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-600">
                                            {(SportTypes as any)[r.sport_type || ''] || r.sport_type || '—'}
                                        </td>
                                        <td className="max-w-[180px] truncate px-3 py-2">{r.owner_name || <span className="text-default-300">—</span>}</td>
                                        <td className="px-3 py-2 tabular-nums">{r.entrants > 0 ? r.entrants : <span className="text-default-300">—</span>}</td>
                                        <td className="px-3 py-2 tabular-nums" title="Finished games (seasons include all divisions)">
                                            {r.games > 0 ? r.games : <span className="text-default-300">—</span>}
                                        </td>
                                        {/* One venue reads inline; several become a button opening the
                                            full split, since a cell can't show it without bloating the row. */}
                                        <td className="max-w-[220px] px-3 py-2">
                                            {r.venues.length === 0
                                                ? <span className="text-default-300">—</span>
                                                : r.venues.length === 1
                                                    ? <span className="flex items-center gap-1.5">
                                                        <span className="truncate">{r.venues[0].name}</span>
                                                        <span className="shrink-0 text-default-400 tabular-nums">{r.venues[0].games}</span>
                                                    </span>
                                                    : <button type="button" onClick={() => setVenueRow(r)}
                                                              title="See the full venue breakdown"
                                                              className="flex w-full items-center gap-1.5 text-left text-primary hover:underline">
                                                        <span className="truncate">{r.venues[0].name}</span>
                                                        <Chip size="sm" variant="flat" color="primary" className="shrink-0">
                                                            +{r.venues.length - 1}
                                                        </Chip>
                                                    </button>}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-default-500">
                                            {r.status != null ? LeagueStatusText(r.status as any) : '—'}
                                        </td>
                                        {/* "Allowed" is not "showing": the carousel still picks for itself
                                            among the allowed ones. The panel above is what is showing. */}
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {(() => {
                                                const isHidden = hiddenOverride[r.uid] ?? !!r.hidden_from_showcase;
                                                return (
                                                    <button type="button" disabled={flipping === r.uid} onClick={() => flipRow(r, !isHidden)}
                                                            title={isHidden ? 'Hidden from the homepage carousel — click to allow it again' : 'May appear on the homepage carousel — click to hide it'}
                                                            className={'rounded-full px-2.5 py-0.5 text-xs font-semibold transition disabled:opacity-50 ' +
                                                                (isHidden ? 'bg-danger-100 text-danger-700 hover:bg-danger-200' : 'bg-default-100 text-default-600 hover:bg-default-200')}>
                                                        {flipping === r.uid ? '…' : isHidden ? 'Hidden' : 'Allowed'}
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {hasMore && rows.length > 0 && (
                    <div className="mt-4 flex justify-center">
                        <button type="button" disabled={query.isFetching}
                                onClick={() => setOffset(rows.length)}
                                className="rounded-full bg-default-100 px-5 py-2 text-sm font-semibold text-default-700 hover:bg-default-200 disabled:opacity-50">
                            {query.isFetching ? 'Loading…' : 'Show 15 more'}
                        </button>
                    </div>
                )}
            </div>

            <VenueBreakdownModal row={venueRow} onClose={() => setVenueRow(null)} />
        </>
    );
}

